/**
 * Manually curated top results for high-traffic search terms.
 *
 * Popular queries like "beach" hit a wall of themes that all score a clean
 * 1.000 concept similarity (we annotated dozens of themes with the same
 * keyword), so the automatic tiebreaks are left arbitrating between rows that
 * are genuinely indistinguishable to the scorer. This map lets us hand-pick
 * which themes lead — and in what order — for those terms.
 *
 * Keys are the lowercased, trimmed search term. Values are entries, best-first
 * (the entry listed first ranks first), in the `printGalleryThemeDisplayOrder`
 * flag's syntax minus its orientation prefix:
 *
 *     themeId[+colorwayId]
 *
 * e.g. `beach_arch` or `beach_arch+sand`. A bare themeId covers every colorway
 * of the theme; `+colorwayId` curates a single colorway.
 *
 * The flag's `tall `/`wide ` prefix is deliberately *not* supported here.
 * Orientation is a property of a template's dimensions, which search never sees
 * — rows are scored per (theme × colorway) against the embeddings index, with no
 * width/height in hand — so an orientation-prefixed entry could only ever fail
 * to match. Curate the theme and let the existing per-template grouping place
 * the orientation variants.
 *
 * Curation supports *literal* keyword matches only. A term earns a 1.000 score
 * by being the exact keyword a theme was annotated with — cosine of a vector
 * with itself — which is why popular terms pile up at a perfect tie in the
 * first place. Synonyms take the semantic path and land well short: "playa"
 * scores ~0.683 against "beach", so a `playa` entry here would be dead code no
 * matter which themes it lists. To curate a synonym, annotate the themes with
 * that keyword instead.
 */
export const CURATED_THEMES_BY_SEARCH_TERM: ReadonlyMap<string, ReadonlyArray<string>> = new Map([
  [
    'beach',
    [
      'amelia',
      'tropicana',
      'tropical_botanical',
      'coastal',
      'sea_breeze',
      'cotton_candy+blue',
      'lush_paradise',
      'toile_de_tropique+aloe',
      'seafoam',
      'cerulean_blush',
      'dream_wash+ocean',
      'carrillo',
      'palm_springs',
      'captain_warren_abdul',
      'tropical_shadows',
      'warm_sands',
      'sea_salt',
      'captain_warren_abdul_ii',
      'jellies_light',
      'malibu',
      'beyond_the_reef',
      'beyond_the_reef_2',
      'porcelain_breeze',
      'island_breeze',
      'musa',
      'jellies_dark',
      'golden_paradise',
      'la_corniche',
      'tropical_dusk',
      'tropical_blossom',
      'deeply',
      'sapphire_tidepool',
      'ocean_stucco',
    ],
  ],
  [
    'destination',
    [
      'vineyard_vignette',
      'amelia',
      'mountain_getaway',
      'tropicana',
      'la_corniche',
      'tropical_botanical',
      'alpine_dream',
      'coastal',
      'sea_breeze',
      'northwest',
      'cotton_candy+blue',
      'artists_garden',
      'sun_arch_i',
      'lush_paradise',
      'mount_baker',
      'toile_de_tropique+aloe',
      'seafoam',
      'estate_light',
      'cactus_rose',
      'cerulean_blush',
      'dream_wash+ocean',
      'desert_daydream',
      'carrillo',
      'palm_springs',
      'frost',
      'talavera',
      'papel_picado',
      'captain_warren_abdul',
      'tropical_shadows',
      'warm_sands',
      'succulent_garden',
      'sea_salt',
      'desert_moonbeam',
      'captain_warren_abdul_ii',
      'jellies_light',
      'papel_moderno',
      'malibu',
      'talavera_azul',
      'beyond_the_reef',
      'beyond_the_reef_2',
      'porcelain_breeze',
      'island_breeze',
      'musa',
      'jellies_dark',
      'golden_paradise',
      'tropical_dusk',
      'tropical_blossom',
      'deeply',
      'sapphire_tidepool',
      'ocean_stucco',
    ],
  ],
  [
    'floral',
    [
      'fiore',
      'maison',
      'regal_garden',
      'ivory_garden',
      'meadow',
      'elegant_flowers',
      'lorange',
      'charlotte',
      'rustic_chic_5',
      'vibrant_garden',
      'bohemian_eucalyptus',
      'lavender_leaves',
      'artists_garden',
      'classical_peonies',
      'bohemian_eucalyptus_photo',
      'sunflowers',
      'soft_blush_2',
      'wonderferris',
      'lavender_yard',
      'citron_frame',
      'chrysanthemum',
      'mucha',
      'pencil_floral_crown',
      'rose_garland_yellow',
      'navy_and_blush',
      'painted_garland',
      'vintage_porcelain',
      'camellia_garden',
      'sakura',
      'lorange_frame',
      'blue_acrylic_floral',
      'lush_paradise',
      'romantic_florals',
      'maison_foil',
      'succulent_sunset',
      'tropical_blossom',
      'boho_arch',
      'rosebed',
      'copper_foil_blooms',
      'storybook',
      'fuchsia_forever',
      'citron',
      'fionas_flowers',
      'vintage_porcelain_foil',
      'magnolia',
      'soft_floral_2',
      'boho_arch_ii',
      'vibrant_crown',
      'copper_foil_blooms_foil',
      'cherry_blossoms_red',
      'spring_flowers',
      'fresh_bouquet',
      'hellebore',
      'floral_garland',
      'cherry_blossoms_pink',
      'merry',
      'peony_woodcut_modern',
      'swan_foil_frame',
      'soft_blush_one',
      'jin_mudan',
      'lush_magenta',
      'zen_flower',
      'peony_woodcut_wild',
      'soft_floral_1',
      'fresh_bouquet_ii',
      'berry_bouquet',
      'burgundy_happiness',
    ],
  ],
  [
    'botanical',
    [
      'bohemian_eucalyptus',
      'tropical_botanical',
      'love_branch',
      'citron_frame',
      'bohemian_eucalyptus_photo',
      'love_and_leaves',
      'rustic_chic_5',
      'botanical_fun',
      'bohemian_eucalyptus_foil',
      'rustic_chic',
      'wreath',
      'lorange',
      'rustic_chic_5_foil',
      'fern_forest',
      'bohemian_eucalyptus_photo_foil',
      'citron',
      'modern_olive',
      'lavender_yard',
      'natural_greenery',
      'cactus_rose',
      'gilded_maple',
      'timeless_devotion',
      'rustic_chic_foil',
      'boho_arch',
      'musa',
      'branching_out',
      'lavender_leaves',
      'gold_foil_frame',
      'painted_garland',
      'lorange_frame',
      'harvest_wreath',
      'succulent_sunset',
      'tropical_blossom',
      'rustic_chic_6',
      'cobalt_glaze',
      'gold_foil_frame_foil',
      'memoir',
      'lush_paradise',
      'evergreen_light',
      'douglas_fir',
      'gilded_maple_foil',
      'lemons_and_flowers',
      'golden_paradise',
      'ginkgo_breeze_gold',
      'rose_foil_frame',
      'rustic_chic_3',
      'eucalyptus_wreath',
      'boho_arch_ii',
      'evergreen_dark',
      'golden_hour',
      'ginkgo_breeze_green',
      'island_breeze',
      'ginkgo_reverie_green',
      'rose_foil_frame_foil',
      'succulent_garden',
      'merry',
      'ginkgo_reverie_gold',
      'golden_hour_foil',
      'magnolia',
      'tropical_dusk',
      'botanique',
      'fawcett',
      'rustic_chic_4',
      'rustic_chic_2',
      'gold_vines',
    ],
  ],
  [
    'typographic',
    [
      'trio_date',
      'quill',
      'deckled_edge',
      'marion',
      'daydream',
      'triptych',
      'penmanship_ii',
      'lumiere',
      'wedding_script',
      'classic_trio',
      'classic_date',
      'concerto',
      'curlicue',
      'longitude_photo',
      'modern_pair',
      'penmanship',
      'felicity',
      'cutline',
      'ampersand_top',
      'romantic_monogram',
      'boldly',
      'longitude',
      'side_cursive',
      'magazine',
      'ever_red',
      'numeric',
      'classic_frame+onyx',
      'daydream_ii',
      'minimalist_arch',
      'lumiere_ii',
      'cosmopolitan',
      'ampersand_top_ii',
      'minimalist_arch_foil',
      'script_dark',
      'editorial',
      'gilded_calligraphy',
      'symphony',
      'newsstand',
      'extra',
      'gilded_calligraphy_foil',
      'big_names',
      'symphony_ii',
      'mulberry',
      'classical_crest',
      'tropicana',
      'tiffany',
      'emilio',
      'parkside',
      'sonata',
      'forever_promise',
      'classic_frame',
      'love_script_gold',
      'interlocked',
      'ampersand',
      'love_script_silver',
      'doodle_frame',
    ],
  ],
  [
    'rustic',
    [
      'vineyard_vignette',
      'love_branch',
      'bohemian_eucalyptus_photo',
      'rustic_chic_5',
      'sunflowers',
      'love_and_leaves',
      'rustic_chic',
      'rustic_chic_5_foil',
      'bohemian_eucalyptus_photo_foil',
      'antique_woodgrain',
      'alpine_dream',
      'love_story+fawn',
      'mount_baker',
      'modern_olive',
      'natural_greenery',
      'meadow+cream',
      'classic_monogram+mocha',
      'bohemian_eucalyptus',
      'mulberry',
      'northwest',
      'boho_arch',
      'rustic_chic_6',
      'abstract_boho',
      'harvest_wreath',
      'rustic_chic_foil',
      'charlotte+mink',
      'memoir',
      'gilded_maple',
      'boho_arch_ii',
      'cellar_door',
      'mono_wreath',
      'frost',
      'rustic_chic_3',
      'harvest_wreath_foil',
      'rustic_rings',
      'rustic_chic_4',
      'marion',
      'rustic_chic_2',
      'gold_vines',
    ],
  ],
  [
    'minimal',
    [
      'classic_monogram',
      'simplicity',
      'deckled_edge',
      'daydream',
      'triptych',
      'love_story+white',
      'penmanship',
      'cutline',
      'longitude+fog',
      'penmanship_ii',
      'longitude_photo+fog',
      'love_branch',
      'minimalist_arch+white',
      'lumiere',
      'classic_trio',
      'classic_date',
      'felicity+white',
      'editorial',
      'concerto',
      'curlicue',
      'modern_pair',
      'formal_deco+white',
      'rustic_chic',
      'magazine+white',
      'numeric',
      'extra+white',
      'newsstand+white',
      'quill+white',
      'cosmopolitan',
      'tiffany+white',
      'boldly',
      'emilio',
      'interlocked+black',
      'ampersand',
      'mulberry',
      'lovely_doves',
      'classic_frame',
      'record_store',
      'ever_red',
      'arch_ii',
      'arch',
      'daydream_ii',
      'lumiere_ii',
      'sonata',
      'script_dark',
      'modern_lines',
      'rustic_chic_6',
      'love_script_gold',
      'forever_promise',
      'deco_sunburst',
      'minimalist_diamond_gold',
      'interlocked_foil',
      'modern_arch',
      'parkside',
      'love_script_rose',
      'endless_time',
      'eggshell',
      'industrial_truss',
      'modern_olive',
      'chic_corners_black',
      'chic_corners_gold',
      'chic_corners_silver',
      'minimalist_diamond_rose',
      'cross_minimal_gold',
      'cross_minimal_silver',
      'star_of_david_minimal_gold',
      'star_of_david_minimal_silver',
      'star_of_david_minimal_blue',
      'foil_mirrored_angles_gold',
      'foil_mirrored_angles_silver',
      'foil_mirrored_angles_rose',
    ],
  ],
  [
    'vintage',
    [
      'quill',
      'marion',
      'maison',
      'emrald_city',
      'formal_deco',
      'antique_woodgrain',
      'mucha',
      'la_luce',
      'classic_frame',
      'classical_crest',
      'charlotte',
      'vintage_porcelain',
      'parkside',
      'bonsoir',
      'art_deco_white',
      'maison_foil',
      'whimsical_bow',
      'toile_de_tropique',
      'chantilly_beige',
      'art_deco_navy',
      'chantilly_dark',
      'gala',
      'silver_gatsby',
      'elegance',
      'mirrorball',
      'record_store',
    ],
  ],
  [
    'whimsical',
    [
      'gala',
      'mirrorball',
      'daydream',
      'enchanted',
      'lovely_doves',
      'lovely_clouds',
      'daydream_ii',
      'la_luce',
      'bonsoir',
      'whimsical_bow',
      'starcrossed',
      'storybook',
      'wavy',
      'doodle_frame',
      'artists_garden',
      'lorien',
      'champagne_sparkle',
      'ever_after_gold',
      'mod_shapes_tropical',
      'abstract_boho',
      'starcrossed_foil',
      'glitz_prism',
      'leopard_mono',
      'ever_after_silver',
      'leopard_gold',
      'neapolitan',
      'mod_shapes_rustic',
      'ever_after_cream',
      'classic_style',
      'cotton_candy',
      'dream_wash',
      'ever_after_slate',
    ],
  ],
  [
    'elegant',
    [
      'romantic_monogram',
      'quill',
      'maison',
      'penmanship_ii',
      'wedding_script',
      'emrald_city',
      'penmanship',
      'regal_garden',
      'bohemian_eucalyptus_photo',
      'formal_deco',
      'felicity',
      'love_story',
      'queens_palace',
      'artists_garden',
      'bohemian_eucalyptus',
      'symphony_ii',
      'charlotte',
      'elegant_flowers',
      'parkside',
      'bohemian_eucalyptus_photo_foil',
      'mucha',
      'classical_crest',
      'sapphire_tidepool',
      'symphony',
      'bohemian_eucalyptus_foil',
      'classic_marble',
      'camellia_garden',
      'rosebed',
      'golden_hour',
      'interlocked',
      'vintage_porcelain',
      'emilio',
      'silk_cranes_1',
      'sonata',
      'hellebore',
      'forever_promise',
      'emerald_tidepool',
      'love_script_gold',
      'chiffon',
      'silver_gatsby',
      'golden_hour_foil',
      'chic_corners_rose',
      'elegant_paper',
      'vintage_porcelain_foil',
      'regency',
      'chantilly_beige',
      'ever_after_cream',
      'chandelier_gold',
    ],
  ],
  [
    'classic',
    [
      'maison',
      'felicity',
      'trio_date',
      'classic_frame',
      'regal_garden',
      'bohemian_eucalyptus',
      'simplicity',
      'classic_date',
      'classical_crest',
      'classical_peonies',
      'concerto',
      'artists_garden',
      'wedding_script',
      'triptych',
      'quill',
      'classic_marble',
      'lumiere',
      'longitude',
      'charlotte',
      'penmanship',
      'emilio',
      'sonata',
      'camellia_garden',
      'arch_i',
      'mucha',
      'ampersand_top',
      'gold_foil_frame',
      'arch_ii',
      'symphony',
      'romantic_monogram',
      'rose_foil_frame',
    ],
  ],
  [
    'fall',
    [
      'gilded_maple',
      'harvest_wreath',
      'sunflowers',
      'gilded_maple_foil',
      'memoir',
      'harvest_wreath_foil',
      'simplicity+chocolate',
      'fawcett',
      'rustic_chic',
      'douglas_fir',
      'rustic_chic_5',
      'antique_woodgrain',
      'classic_monogram+fawn',
      'rustic_chic_foil',
      'rustic_chic_5_foil',
      'love_branch',
      'chrysanthemum',
      'rose_foil_frame',
      'golden_hour',
      'til_death',
      'starcrossed',
      'starcrossed_foil',
      'hellebore',
      'minimalist_arch+mocha',
      'quill+burgundy',
      'mulberry',
      'emilio+mocha',
      'cosmopolitan+mocha',
      'editorial+mocha',
      'numeric+mocha',
      'boldly+orange',
      'emilio+mustard',
      'newsstand+terra',
      'extra+blush',
      'fern_forest',
      'northwest',
      'love_story+burgundy',
    ],
  ],
  [
    'mountain',
    [
      'mountain_getaway',
      'alpine_dream',
      'la_corniche',
      'northwest',
      'mount_baker',
      'vineyard_vignette',
      'frost',
      'douglas_fir',
      'rustic_rings',
      'desert_moonbeam',
      'desert_daydream',
    ],
  ],
  [
    'winter',
    [
      'frost',
      'douglas_fir',
      'merry',
      'wreath',
      'evergreen_dark',
      'evergreen_light',
      'gilded_maple+olive',
      'light_snow',
      'snow_lake',
      'harvest_wreath',
      'alpine_dream',
      'northwest',
      'rustic_rings',
      'fern_forest',
      'elegant_flowers',
      'gilded_maple_foil',
    ],
  ],
  ['chinese', ['double_happiness_calligraphy', 'double_happiness', 'burgundy_happiness', 'cherry_blossoms_pink', 'cherry_blossoms_red', 'sakura']],
  [
    'tropical',
    [
      'tropicana',
      'tropical_botanical',
      'lush_paradise',
      'toile_de_tropique',
      'island_breeze',
      'golden_paradise',
      'malibu',
      'sea_breeze',
      'tropical_blossom',
      'musa',
      'tropical_shadows',
      'carrillo',
      'lorange',
      'warm_sands',
      'seafoam',
      'lorange_frame',
      'tropical_dusk',
      'amelia',
      'coastal',
      'palm_springs',
      'cotton_candy',
      'dream_wash',
      'sea_salt',
      'porcelain_breeze',
      'beyond_the_reef',
      'beyond_the_reef_2',
      'deeply',
      'sapphire_tidepool',
      'ocean_stucco',
      'captain_warren_abdul',
      'captain_warren_abdul_ii',
      'jellies_dark',
      'jellies_light',
      'quill',
      'citron',
      'citron_frame',
    ],
  ],
  [
    'ocean',
    [
      'carrillo',
      'seafoam',
      'amelia',
      'ocean_stucco',
      'sea_breeze',
      'sea_salt',
      'porcelain_breeze',
      'coastal',
      'sapphire_tidepool',
      'dream_wash',
      'deeply',
      'cerulean_blush',
      'toile_de_tropique+ocean',
      'tropicana+ocean',
      'cotton_candy',
      'tropical_shadows',
      'la_corniche',
      'jellies_dark',
      'jellies_light',
      'beyond_the_reef',
      'beyond_the_reef_2',
      'malibu',
    ],
  ],
]);

/**
 * Similarity at or above which a row counts as a "clean match" eligible for
 * curation. Concept cosines top out at 1, but accumulated float math (and the
 * 1.01 lexical boost) means an exact hit can land a hair off, so compare with a
 * tolerance rather than `=== 1`.
 */
const PERFECT_SIMILARITY = 1 - 1e-6;

export const isPerfectSimilarity = (similarity: number): boolean => similarity >= PERFECT_SIMILARITY;

/** Identity of a scored row, matched against a parsed curated entry. `colorwayId` is absent for colorway-less rows. */
export type CuratedRowIdentity = Readonly<{
  themeId: string;
  colorwayId?: string;
}>;

/**
 * Splits a curated entry into theme and colorway.
 *
 * The `+` delimiter is explicit on purpose, and is why this shares the flag's
 * grammar rather than inventing one: themeIds themselves contain `_`
 * (`modern_lines`, `upload_your_design`) and some color-suffixed strings are real
 * legacy themeIds (`emilio_sage`), so `_` cannot be parsed unambiguously.
 */
const parseCuratedEntry = (entry: string): { themeId: string; colorwayId?: string } => {
  const plusIndex = entry.indexOf('+');
  const themeId = (plusIndex === -1 ? entry : entry.slice(0, plusIndex)).trim();
  const colorwayId = plusIndex === -1 ? undefined : entry.slice(plusIndex + 1).trim() || undefined;
  return { themeId, colorwayId };
};

const matchesEntry = (entry: string, row: CuratedRowIdentity): boolean => {
  const { themeId, colorwayId } = parseCuratedEntry(entry);
  if (themeId !== row.themeId) return false;
  // A bare themeId covers every colorway of the theme; `+colorwayId` narrows to one.
  return !colorwayId || colorwayId === row.colorwayId;
};

/**
 * Rank of `row` in the curated list for `searchTerm`, or
 * `Number.POSITIVE_INFINITY` when it isn't curated — so uncurated rows sort
 * after every curated one and stay tied with each other, deferring to the lower
 * precedence rules.
 *
 * When several entries match a row (e.g. a bare `boldly` and a narrower
 * `boldly+tomato`), the earliest wins: a row ranks by the best position it was
 * curated into.
 */
export function getCuratedRank(searchTerm: string | undefined, row: CuratedRowIdentity): number {
  if (!searchTerm) return Number.POSITIVE_INFINITY;
  const curated = CURATED_THEMES_BY_SEARCH_TERM.get(searchTerm.trim().toLowerCase());
  if (!curated) return Number.POSITIVE_INFINITY;
  const index = curated.findIndex(entry => matchesEntry(entry, row));
  return index === -1 ? Number.POSITIVE_INFINITY : index;
}
